import cn from 'classnames';
import { isAfter, isEqual, parse } from 'date-fns';
import { FormState } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { Dispatch, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';

import {
  ISystemInoperability,
  ISystemInoperabilityForm,
  ISystemInoperabilityFormValidation,
  ISystemInoperabilityStoreProps,
} from 'APIPackage/SystemInoperabilityModule/interfaces/ISystemInoperability';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { sortMapper } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortOrderBySelected } from 'CorePackage/MainModule/common/getSortOrderBySelected';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { Button } from 'UIPackage/MainModule/components/Button';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { SYSTEM_INOPERABILITY_NOT_ALL_SAVED_MESSAGE } from 'CorePackage/MainModule/constants/notices';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import {
  adminGetSystemInoperabilityList,
  adminGetSystemInoperabilityListNextPage,
  adminSetSystemInoperabilityAddRecord,
  adminSetSystemInoperabilityFormUnsavedRecords,
  adminSetSystemInoperabilityListOrder,
  adminSetSystemInoperabilityListShowSave,
} from '../actions/adminAction';
import { initialSystemInoperabilityTableColumns } from '../constants/initialSystemInoperabilityTableColumns';
import {
  getAdminSystemInoperabilityFormElementRef,
  getAdminSystemInoperabilityFormRef,
  getAdminSystemInoperabilityFormUnsavedRecords,
  getAdminSystemInoperabilityList,
  getAdminSystemInoperabilityListIsShowSave,
  getAdminSystemInoperabilityListPage,
  getAdminSystemInoperabilityTotalCount,
} from '../selectors/adminSelectors';
import styles from './NewsTable.module.scss';
import { RenderForm } from './SystemInoperabilityForm';
import tableStyles from './SystemInoperabilityTable.module.scss';

const SystemInoperabilityTableWithoutStore = ({
  formRef,
  systemInoperabilityList,
  showSaveButton,
  unsavedRecords,
  totalCount,
  loading,
  formElementRef,
  page,
}: ISystemInoperabilityStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const theadDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, styles.TheadCell);
  const theadReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, styles.TheadCell);
  const tbodyRowClassName = cn(styles.Row, styles.TbodyRow);
  const tbodyDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, styles.TbodyCell);
  const tbodyReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, styles.TbodyCell);
  const [tableColumns, setTableColumns] = useState<ITableColumn<ISystemInoperability>[]>(
    initialSystemInoperabilityTableColumns,
  );
  const push = formRef?.current.mutators.push;
  const isSortAvailable = useRef(true);

  useEffect(() => {
    if (formRef && unsavedRecords && unsavedRecords.length > 0) {
      const values = formRef.current.getState()?.values;

      if (values && Object.keys(values).length === 0) {
        const records = unsavedRecords.map(record => {
          return {
            startDate: record.startDate,
            endDate: record.endDate,
            reason: record.reason,
          } as ISystemInoperability;
        });

        formRef?.current.initialize({ records });
        dispatch(adminSetSystemInoperabilityFormUnsavedRecords([]));
        setTimeout(() => {
          formRef?.current.submit();
        }, 0);
        dispatch(
          addNoticeAction({
            renderContent: SYSTEM_INOPERABILITY_NOT_ALL_SAVED_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADMIN_ADD_SYSTEM_INOPERABILITY_RECORD,
          }),
        );
      }
    }
  }, [formRef, unsavedRecords, push, dispatch]);

  useEffect(() => {
    formRef?.current.subscribe(
      (state: FormState<ISystemInoperabilityForm>) => {
        if (state.values?.records && state.values.records.length > 0) {
          isSortAvailable.current = false;
          dispatch(adminSetSystemInoperabilityListShowSave(true));
        } else {
          isSortAvailable.current = true;
          dispatch(adminSetSystemInoperabilityListShowSave(false));
          dispatch(adminSetSystemInoperabilityFormUnsavedRecords([]));
        }
      },
      { pristine: true, invalid: true, values: true },
    );
  }, [formRef, dispatch]);

  useEffect(() => {
    dispatch(adminGetSystemInoperabilityList());
  }, [dispatch]);

  const handleSortClick = useCallback(
    (e: MouseEvent<HTMLElement>): void => {
      if (isSortAvailable.current === true) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { id } = (e.target as any).dataset;
        const newColumnsData = handleSortChange(tableColumns, id as keyof ISystemInoperability);

        setTableColumns(newColumnsData);
        const newOrder = getSortOrderBySelected(newColumnsData, sortMapper);

        dispatch(adminSetSystemInoperabilityListOrder(newOrder));
        dispatch(adminGetSystemInoperabilityList());
      }
    },
    [dispatch, tableColumns],
  );

  const addRecord = useCallback(() => {
    const formRecord: ISystemInoperability = {
      startDate: '',
      endDate: '',
      reason: '',
    };

    push?.('records', formRecord);
  }, [push]);

  const onLoadMore = useCallback(() => dispatch(adminGetSystemInoperabilityListNextPage()), [dispatch]);

  const table = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (page <= 0) {
      return;
    }
    setTimeout(() => {
      table.current?.scrollIntoView(false);
    }, 0);
  }, [page]);

  const validateForm = useCallback((value: ISystemInoperabilityFormValidation | undefined) => {
    const emptyMessage = 'Поле не может быть пустым';
    const errors: ISystemInoperabilityFormValidation = {
      records: [],
    };

    value?.records?.forEach(record => {
      const periodStart = parseDate({ date: record?.startDate ?? '', parseFormat: DATE_FORMAT_ISO_FNS });
      const periodEnd = parseDate({ date: record?.endDate ?? '', parseFormat: DATE_FORMAT_ISO_FNS });

      const error: Partial<ISystemInoperability> = {};

      if (!record.reason?.trim()) {
        error.reason = emptyMessage;
      }
      if (!record.startDate) {
        error.startDate = emptyMessage;
      }
      if (!record.endDate) {
        error.endDate = emptyMessage;
      }
      if (periodStart && periodEnd && (isAfter(periodStart, periodEnd) || isEqual(periodStart, periodEnd))) {
        error.startDate = 'Начало периода должно быть меньше окончания периода';
        error.endDate = 'Окончание периода должно быть больше начала периода';
      }
      errors.records.push(error);
    });

    return errors;
  }, []);

  const dispatchSubmitOnForm = useCallback(() => {
    formElementRef?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }, [formElementRef]);

  const saveRecords = useCallback(
    (value: ISystemInoperabilityForm) => {
      const tzoffset = new Date().getTimezoneOffset() * 60000;
      const formData = value.records?.map(record => ({
        reason: record.reason?.trim(),
        startDate: new Date(
          parse(record.startDate ?? '', DATE_FORMAT_LONG_MINUTE_FNS, new Date()).getTime() - tzoffset,
        )?.toISOString(),
        endDate: new Date(
          parse(record.endDate ?? '', DATE_FORMAT_LONG_MINUTE_FNS, new Date()).getTime() - tzoffset,
        )?.toISOString(),
      }));

      if (formData) {
        dispatch(adminSetSystemInoperabilityAddRecord(formData));
      }
    },
    [dispatch],
  );

  const submitForm = useCallback(() => {
    const formState = formRef?.current.getState();

    if (formState) {
      const { valid: isValid, values } = formState;

      if (isValid) {
        // Вызов Submit вызовет и сохранение формы
        dispatchSubmitOnForm();
      } else {
        // Вызов Submit обновит валидацию, но не вызовет сохранения
        // Требуется принудительное сохранение
        const errors = validateForm(values);

        dispatchSubmitOnForm();
        saveRecords(values);

        return errors;
      }
    }
  }, [dispatchSubmitOnForm, formRef, saveRecords, validateForm]);

  return (
    <div className={tableStyles.TableWrapper}>
      <div className={styles.Header}>
        <h1 className={styles.Title}>Периоды неработоспособности системы</h1>
        <div>
          {showSaveButton && (
            <Button
              className={tableStyles.FormButton}
              color="primary"
              text="Сохранить"
              id="saveButton"
              type="submit"
              onClick={submitForm}
            />
          )}
          <Button
            className={tableStyles.FormButton}
            color="success"
            text="Добавить период"
            id="addButton"
            onClick={addRecord}
          />
        </div>
      </div>
      <table className={tableStyles.Table} ref={table}>
        <thead className={tableStyles.TableHead}>
          <tr className={styles.Row}>
            {tableColumns.map((row, i) => (
              <th
                onClick={handleSortClick}
                data-id={row.id}
                className={i < 2 ? theadDateCellClassName : theadReasonCellClassName}
                key={i}
              >
                {row.name}
                {getSortIcon(tableColumns, row.id)}
              </th>
            ))}
          </tr>
        </thead>
        {!loading && (
          <tbody>
            <tr className={styles.Row}>
              <td colSpan={3} className={tableStyles.FormContainerCell}>
                <Form
                  onSubmit={saveRecords}
                  validate={validateForm}
                  render={RenderForm}
                  mutators={{
                    ...arrayMutators,
                  }}
                />
              </td>
            </tr>
            {systemInoperabilityList.map((row, i) => (
              <tr className={tbodyRowClassName} key={i}>
                <td className={tbodyDateCellClassName}>
                  {formatDate({
                    date: row.startDate ?? '',
                    dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                    parseFormat: DATE_FORMAT_ISO_FNS,
                  })}
                </td>
                <td className={tbodyDateCellClassName}>
                  {formatDate({
                    date: row.endDate ?? '',
                    dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                    parseFormat: DATE_FORMAT_ISO_FNS,
                  })}
                </td>
                <td className={tbodyReasonCellClassName}>{row.reason}</td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {systemInoperabilityList.length < totalCount && (
        <Button type="button" text="Показать еще" color="load" onClick={onLoadMore} />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): ISystemInoperabilityStoreProps => ({
  formRef: getAdminSystemInoperabilityFormRef(state),
  systemInoperabilityList: getAdminSystemInoperabilityList(state),
  showSaveButton: getAdminSystemInoperabilityListIsShowSave(state),
  unsavedRecords: getAdminSystemInoperabilityFormUnsavedRecords(state),
  totalCount: getAdminSystemInoperabilityTotalCount(state),
  loading: getCoreIsLoading(state),
  formElementRef: getAdminSystemInoperabilityFormElementRef(state),
  page: getAdminSystemInoperabilityListPage(state),
});

export const SystemInoperabilityTable = connect(mapStateToProps)(SystemInoperabilityTableWithoutStore);
